import { Injectable, inject } from '@angular/core';
import { EXT_PREFIX, FHIR_PUBLISHER, QUESTIONNAIRE_PREFIX } from '../constants/fhir.constants';
import {
  ConversionError,
  ConversionOptions,
  ConversionResult,
  DesignerQuestionnaireOptions,
  FHIRExtensionConfig,
  FHIR_CONVERTER_CONFIG,
  IControlCard,
  IFhirConverterService,
  ProgressCallback,
} from '../interfaces/fhir-converter.interfaces';
import { FHIRItem, FHIRQuestionnaire } from '../models/fhir.model';
import { Template } from '../models/template.model';
import { DesignerFhirConverter } from './designer-fhir.converter';
import { FhirValidator } from './fhir.validator';
import { TemplateFhirConverter } from './template-fhir.converter';

/** Default extension configuration */
const DEFAULT_CONFIG: FHIRExtensionConfig = {
  prefix: EXT_PREFIX,
  uiConfig: 'ui',
  questionnairePrefix: QUESTIONNAIRE_PREFIX,
  publisher: FHIR_PUBLISHER,
};

/**
 * FHIR Converter Service - Facade
 *
 * This is the main entry point for FHIR conversion operations. It delegates to specialized converters while providing a
 * unified API.
 *
 * Note: Not providedIn: 'root' because it needs to be provided in the app context where CONTROL_METADATA_PROVIDER is
 * configured.
 */
@Injectable()
export class FhirConverterService implements IFhirConverterService<Template> {
  private readonly designerConverter = inject(DesignerFhirConverter);
  private readonly templateConverter = inject(TemplateFhirConverter);
  private readonly validator = inject(FhirValidator);
  private readonly injectedConfig = inject(FHIR_CONVERTER_CONFIG, { optional: true });

  private readonly config: FHIRExtensionConfig;

  constructor() {
    this.config = { ...DEFAULT_CONFIG, ...this.injectedConfig };
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  getConfig(): Readonly<FHIRExtensionConfig> {
    return { ...this.config };
  }

  clearCache(): void {
    this.designerConverter.clearCache();
  }

  // ============================================================================
  // DESIGNER CONVERSION
  // ============================================================================

  convertControlToFhirItem(control: IControlCard, index: number, parentPath = ''): FHIRItem | null {
    return this.designerConverter.convertControl(control, index, parentPath);
  }

  convertDesignerControlsToFhirItems(controls: IControlCard[], parentPath = ''): FHIRItem[] {
    return this.designerConverter.convertControls(controls, parentPath);
  }

  generateDesignerFhirQuestionnaire(options: DesignerQuestionnaireOptions): string {
    return this.designerConverter.generateQuestionnaire(options);
  }

  convertToFhirWithResult(
    options: DesignerQuestionnaireOptions,
    conversionOptions: ConversionOptions = {}
  ): ConversionResult<string> {
    return this.designerConverter.convertWithResult(options, conversionOptions);
  }

  // ============================================================================
  // TEMPLATE CONVERSION
  // ============================================================================

  /**
   * Convert FHIR Questionnaire to web.template Builder Template model This is specifically for the template builder in
   * web.template package Returns a Template object compatible with BuilderStore
   *
   * @param questionnaire - FHIRQuestionnaire to convert
   * @returns Template object for builder
   */
  fhirToBuilderTemplate(questionnaire: FHIRQuestionnaire, template: Template): Template {
    return this.templateConverter.fhirToBuilderTemplate(questionnaire, template);
  }

  // ============================================================================
  // BATCH OPERATIONS
  // ============================================================================

  async batchConvert(
    templates: DesignerQuestionnaireOptions[],
    options: ConversionOptions = {},
    onProgress?: ProgressCallback
  ): Promise<ConversionResult<string>[]> {
    const results: ConversionResult<string>[] = [];
    const total = templates.length;

    for (let i = 0; i < total; i++) {
      const template = templates[i];
      onProgress?.({ current: i + 1, total, item: template.title });

      const result = this.convertToFhirWithResult(template, options);
      results.push(result);

      if (i % 10 === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }

    return results;
  }

  // ============================================================================
  // VALIDATION
  // ============================================================================

  validateQuestionnaire(questionnaire: unknown, errors: ConversionError[] = [], warnings: string[] = []): boolean {
    return this.validator.validate(questionnaire, errors, warnings);
  }

  /**
   * Validate controls for static dataMode Returns array of linkIds that have dataMode set to 'static' These controls
   * should use API mode instead when saving to FHIR
   */
  validateNoStaticDataMode(controls: IControlCard[]): string[] {
    return this.designerConverter.validateNoStaticDataMode(controls);
  }
}
